import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Checkbox
} from '@material-ui/core';
import {
  CheckCircle as ConfirmIcon,
  Unlock as ReleaseIcon,
  DollarSign as PayIcon,
  XCircle as CancelIcon
} from 'react-feather';
import {
  COMMISSION_STATUS,
  getStatusLabel,
  getStatusColor
} from '../../utils/commissionStatus';

const TERMINAL_STATUSES = [COMMISSION_STATUS.PAID, COMMISSION_STATUS.CANCELLED];

const CommissionsListResults = ({
  regs,
  page,
  setPage,
  perPage,
  setPerPage,
  total,
  onConfirmPayment,
  onRelease,
  onPay,
  onCancel,
  isAdmin,
  selected,
  setSelected,
  bulkMode,
  ...rest
}) => {
  const handleLimitChange = (event) => {
    setPerPage(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);

  // Define qual status a linha precisa ter pra poder ser marcada em bulk
  let bulkSelectableStatus = null;
  if (bulkMode === 'release') {
    bulkSelectableStatus = COMMISSION_STATUS.PENDING_RELEASE;
  } else if (bulkMode === 'pay') {
    bulkSelectableStatus = COMMISSION_STATUS.RELEASED;
  }

  const selectableRegs = bulkSelectableStatus
    ? regs.filter((c) => c.status === bulkSelectableStatus)
    : [];

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(selectableRegs.map((c) => c.id));
      return;
    }
    setSelected([]);
  };

  const handleSelectOne = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const renderActions = (commission) => {
    if (!isAdmin || TERMINAL_STATUSES.includes(commission.status)) {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        {commission.status === COMMISSION_STATUS.PENDING_PAYMENT && (
          <Tooltip title="Confirmar Pagamento">
            <IconButton
              size="small"
              sx={{ color: 'info.main' }}
              onClick={() => onConfirmPayment(commission.id)}
            >
              <ConfirmIcon size={18} />
            </IconButton>
          </Tooltip>
        )}

        {commission.status === COMMISSION_STATUS.PENDING_RELEASE && (
          <Tooltip title="Liberar">
            <IconButton
              size="small"
              sx={{ color: 'primary.main' }}
              onClick={() => onRelease(commission.id)}
            >
              <ReleaseIcon size={18} />
            </IconButton>
          </Tooltip>
        )}

        {commission.status === COMMISSION_STATUS.RELEASED && (
          <Tooltip title="Marcar como Paga">
            <IconButton
              size="small"
              sx={{ color: 'success.main' }}
              onClick={() => onPay(commission.id)}
            >
              <PayIcon size={18} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Cancelar">
          <IconButton
            size="small"
            sx={{ color: 'error.main' }}
            onClick={() => onCancel(commission.id)}
          >
            <CancelIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Card {...rest}>
      <PerfectScrollbar>
        <Box sx={{ minWidth: 1050 }}>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && bulkMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectableRegs.length > 0 && selected.length === selectableRegs.length}
                      indeterminate={selected.length > 0 && selected.length < selectableRegs.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>Venda</TableCell>
                <TableCell>Afiliado</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Link</TableCell>
                {isAdmin && <TableCell>Valor Venda</TableCell>}
                <TableCell>Comissão</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data</TableCell>
                {isAdmin && <TableCell align="center">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {regs.map((commission) => {
                const isSelected = selected.indexOf(commission.id) !== -1;
                const canSelect = bulkSelectableStatus && commission.status === bulkSelectableStatus;

                return (
                  <TableRow hover key={commission.id} selected={isSelected}>
                    {isAdmin && bulkMode && (
                      <TableCell padding="checkbox">
                        {canSelect && (
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectOne(commission.id)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography color="textPrimary" variant="body2" sx={{ fontWeight: 'bold' }}>
                        #{commission.sale?.external_order_id || 'N/A'}
                      </Typography>
                      <Typography color="textSecondary" variant="body2" fontSize={12}>
                        ID: {commission.sale?.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textPrimary" variant="body2">
                        {commission.sale?.affiliateLink?.channel?.user?.name || 'N/A'}
                      </Typography>
                      <Typography color="textSecondary" variant="body2" fontSize={12}>
                        {commission.sale?.affiliateLink?.channel?.user?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textPrimary" variant="body2">
                        {commission.sale?.affiliateLink?.channel?.name || 'N/A'}
                      </Typography>
                      <Typography color="textSecondary" variant="body2" fontSize={12}>
                        {commission.sale?.affiliateLink?.channel?.platform}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textPrimary" variant="body2" sx={{ fontWeight: 'bold' }}>
                        {commission.sale?.affiliateLink?.code || 'N/A'}
                      </Typography>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <Typography color="textPrimary" variant="body2">
                          {formatCurrency(commission.sale?.amount || 0)}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography
                        color="textPrimary"
                        variant="body1"
                        sx={{ fontWeight: 'bold', color: 'success.main' }}
                      >
                        {formatCurrency(commission.amount)}
                      </Typography>
                      <Typography color="textSecondary" variant="body2" fontSize={12}>
                        {commission.percentage}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(commission.status)}
                        color={getStatusColor(commission.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography color="textPrimary" variant="body2">
                        {moment(commission.created_at).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    {isAdmin && <TableCell align="center">{renderActions(commission)}</TableCell>}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={page - 1}
        rowsPerPage={perPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Registros por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
        }
      />
    </Card>
  );
};

CommissionsListResults.propTypes = {
  regs: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  setPerPage: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  onConfirmPayment: PropTypes.func,
  onRelease: PropTypes.func,
  onPay: PropTypes.func,
  onCancel: PropTypes.func,
  isAdmin: PropTypes.bool,
  selected: PropTypes.array,
  setSelected: PropTypes.func,
  bulkMode: PropTypes.oneOf(['release', 'pay', null])
};

export default CommissionsListResults;
